import { fetchUtils, HttpError } from "react-admin";
import simpleRestProvider from "ra-data-simple-rest";
import { stringify } from "query-string";
import { RESTAURANT_TYPES } from "./enums/RestaurantTypes";
import { ENTITY_AVAILABILITY_STATUS } from "./enums/EntityAvailabilityStatus";

const { REACT_APP_BASE_URL } = process.env;

export const UPLOADED_IMAGES_KEY = "uploadedImages";
export const UPLOADED_RESTAURANT_COVER_IMAGES_KEY = "uploadedCoverImages";
export const UPLOADED_RESTAURANT_PRODUCT_IMAGES_KEY = "uploadedProductImages";
export const UPLOADED_VIDEOS_THUMBNAIL_KEY = "uploadedThumbnail";
export const UPLOADED_COMMUNITY_POST_IMAGES_KEY = "uploadedCommunityPostImages";

const httpClient = (url, options = {}) => {
  const token = localStorage.getItem("token");
  if (!token) return Promise.reject();

  if (!options.headers) {
    options.headers = new Headers({ Accept: "application/json" });
  }
  options.headers.set("Authorization", `Bearer ${token}`);
  return fetchUtils.fetchJson(url, options);
};

const originalDataProvider = simpleRestProvider(
  `${REACT_APP_BASE_URL}`,
  httpClient
);

// Cache the backend error-code translations so non-React code (this data
// provider) can translate error codes the same way components do via
// useTranslation(). Same endpoint the TranslationProvider consumes.
let errorTranslations = {};
fetch(`${REACT_APP_BASE_URL}/translations/es`)
  .then((response) => (response.ok ? response.json() : {}))
  .then((data) => {
    errorTranslations = data || {};
  })
  .catch(() => {});

const getErrorMessage = (error) => {
  const errorCode = error?.body?.errors?.[0]?.msg || error?.body?.message;

  return `Ha ocurrido un error: ${
    errorTranslations[errorCode] ||
    errorCode ||
    "Consulte la consola para más detalles."
  }`;
};

export const checkAuthError = (errorStatus) => {
  if (errorStatus === 401 || errorStatus === 403) {
    localStorage.clear();
    window.location.replace("/#/login");
    return true;
  }
  return false;
};

const transformToFormData = (data, resource) => {
  let formData = new FormData();
  for (let key in data) {
    if (typeof data[key] === "undefined" || data[key] === null) continue;
    if (Array.isArray(data[key]) && data[key].some((i) => i?.rawFile)) {
      // Array of images
      if (resource === "products") {
        if (key === "images") {
          const arrUrls = data[key].filter((i) => !i.rawFile);
          formData.append(key, JSON.stringify(arrUrls));

          const arrFiles = data[key]
            .filter((i) => i.rawFile)
            .map((i) => i.rawFile);
          arrFiles.forEach((i) => {
            formData.append(UPLOADED_IMAGES_KEY, i);
          });
        }
      } else if (resource === "restaurants") {
        if (key === "coverImages" || key === "productImages") {
          const arrUrls = data[key].filter((i) => !i.rawFile);
          formData.append(key, JSON.stringify(arrUrls));

          const arrFiles = data[key]
            .filter((i) => i.rawFile)
            .map((i) => i.rawFile);
          arrFiles.forEach((i) => {
            formData.append(
              key === "coverImages"
                ? UPLOADED_RESTAURANT_COVER_IMAGES_KEY
                : UPLOADED_RESTAURANT_PRODUCT_IMAGES_KEY,
              i
            );
          });
        }
      } else if (resource === "community-posts") {
        if (key === "images") {
          const arrUrls = data[key].filter((i) => !i.rawFile);
          formData.append(key, JSON.stringify(arrUrls));

          const arrFiles = data[key]
            .filter((i) => i.rawFile)
            .map((i) => i.rawFile);
          arrFiles.forEach((i) => {
            formData.append(UPLOADED_COMMUNITY_POST_IMAGES_KEY, i);
          });
        }
      }
    } else if (data[key]?.rawFile) {
      if (resource === "videos" && key === "thumbnail") {
        formData.append(UPLOADED_VIDEOS_THUMBNAIL_KEY, data[key].rawFile);
      } else {
        formData.append(key, data[key].rawFile);
      }
    } else if (Array.isArray(data[key]) || typeof data[key] === "object") {
      formData.append(key, JSON.stringify(data[key]));
    } else {
      formData.append(key, data[key]);
    }
  }
  return formData;
};

const dataProvider = {
  ...originalDataProvider,
  getOne: (resource, params) => {
    if (resource === "restaurants-draft") {
      resource = "restaurants";
    }
    return originalDataProvider.getOne(resource, params);
  },
  getList: (resource, params) => {
    if (resource === "restaurants-draft") {
      resource = "restaurants";
      params.filter = { ...params.filter, type: RESTAURANT_TYPES.DRAFT };
    }

    return originalDataProvider.getList(resource, params);
  },
  getMany: (resource, params) => {
    const filter = { id: params.ids };
    if (resource === "restaurants") {
      filter.type = RESTAURANT_TYPES.ALL;
    }

    if (resource === "restaurants-draft") {
      filter.type = RESTAURANT_TYPES.DRAFT;
      resource = "restaurants";
    }

    const query = {
      filter: JSON.stringify(filter),
      range: JSON.stringify([0, 999]),
    };
    const url = `${REACT_APP_BASE_URL}/${resource}?${stringify(query)}`;
    return httpClient(url).then(({ json }) => ({ data: json }));
  },
  update: (resource, params) => {
    if (
      resource !== "categories" &&
      resource !== "regions" &&
      resource !== "place-types" &&
      resource !== "place-targets" &&
      resource !== "restaurants" &&
      resource !== "products" &&
      resource !== "districts" &&
      resource !== "banners" &&
      resource !== "videos" &&
      resource !== "restaurants-draft" &&
      resource !== "community-posts"
    ) {
      return originalDataProvider.update(resource, params);
    }

    if (resource === "products" && params.data.images) {
      if (!params.data.images) {
        params.data.images = [];
      }

      params.data.images = [
        ...params.data.images.filter((i) => !i.rawFile).map((i) => i.url),
        ...params.data.images.filter((i) => i.rawFile),
      ];
    } else if (resource === "restaurants" || resource === "restaurants-draft") {
      if (resource === "restaurants-draft") {
        resource = "restaurants";
        params.data.type = RESTAURANT_TYPES.DRAFT;
      }

      if (params.data.coverImages) {
        params.data.coverImages = [
          ...params.data.coverImages
            .filter((i) => !i.rawFile)
            .map((i) => i.url),
          ...params.data.coverImages.filter((i) => i.rawFile),
        ];
      }
      if (params.data.productImages) {
        params.data.productImages = [
          ...params.data.productImages
            .filter((i) => !i.rawFile)
            .map((i) => i.url),
          ...params.data.productImages.filter((i) => i.rawFile),
        ];
      }
      if (
        params.data.billingData &&
        Object.values(params.data.billingData).filter((e) => e).length === 0
      ) {
        params.data.billingData = undefined;
      }
    } else if (resource === "community-posts") {
      if (params.data.images) {
        params.data.images = [
          ...params.data.images.filter((i) => !i.rawFile).map((i) => i.url),
          ...params.data.images.filter((i) => i.rawFile),
        ];
      }
    }

    if (resource === "regions" && params.data.points?.type === "Polygon") {
      params.data.points = params.data.points.coordinates[0].map(
        ([lng, lat]) => ({ lat, lng })
      );
    }

    const formData = transformToFormData(params.data, resource);

    return httpClient(`${REACT_APP_BASE_URL}/${resource}/${params.data.id}`, {
      method: "PUT",
      body: formData,
    })
      .then(({ json }) => ({
        data: { ...params.data, id: json.id },
      }))
      .catch((err) => {
        const errorMsg = getErrorMessage(err);
        return Promise.reject(new HttpError(errorMsg, err.status, err.body));
      });
  },
  create: (resource, params) => {
    const originalResource = resource;
    if (
      resource !== "categories" &&
      resource !== "regions" &&
      resource !== "place-types" &&
      resource !== "place-targets" &&
      resource !== "restaurants" &&
      resource !== "products" &&
      resource !== "users" &&
      resource !== "banners" &&
      resource !== "billing" &&
      resource !== "products-draft" &&
      resource !== "community-posts"
    ) {
      return originalDataProvider.create(resource, params);
    }

    if (resource === "users") {
      return originalDataProvider.create(`auth/register/restaurant`, params);
    }

    if (resource === "products-draft") {
      resource = "products";
    }

    if (resource === "products" && params.data.images) {
      params.data.images = [
        ...params.data.images.filter((i) => !i.rawFile).map((i) => i.url),
        ...params.data.images.filter((i) => i.rawFile),
      ];
    } else if (resource === "restaurants") {
      if (params.data.coverImages) {
        params.data.coverImages = [
          ...params.data.coverImages
            .filter((i) => !i.rawFile)
            .map((i) => i.url),
          ...params.data.coverImages.filter((i) => i.rawFile),
        ];
      }
      if (params.data.productImages) {
        params.data.productImages = [
          ...params.data.productImages
            .filter((i) => !i.rawFile)
            .map((i) => i.url),
          ...params.data.productImages.filter((i) => i.rawFile),
        ];
      }
      if (
        params.data.billingData &&
        Object.values(params.data.billingData).filter((e) => e).length === 0
      ) {
        params.data.billingData = undefined;
      }
    }
    const formData = transformToFormData(params.data, resource);

    return httpClient(`${REACT_APP_BASE_URL}/${resource}`, {
      method: "POST",
      body: formData,
    })
      .then(({ json }) => {
        if (resource === "billing")
          return {
            data: { ...json, id: json.id },
          };
        return {
          data: { ...params.data, id: json.id },
        };
      })
      .then((data) => {
        if (originalResource === "products-draft") {
          window.location.href = `/#/products/${data.data.id}/show`;
        }
        return data;
      })
      .catch((err) => {
        const errorMsg = getErrorMessage(err);
        return Promise.reject(new HttpError(errorMsg, err.status, err.body));
      });
  },
  delete: (resource, params) => {
    return httpClient(`${REACT_APP_BASE_URL}/${resource}/${params.id}`, {
      method: "PUT",
      body: JSON.stringify({ ...params.previousData, active: false }),
    })
      .then(({ json }) => ({ data: json }))
      .catch((err) => {
        const errorMsg = getErrorMessage(err);
        return Promise.reject(new HttpError(errorMsg, err.status, err.body));
      });
  },
  duplicate: (resource, params) => {
    if (resource !== "restaurants") {
      return;
    }
    return httpClient(
      `${REACT_APP_BASE_URL}/${resource}/${params.id}/duplicate`,
      {
        method: "POST",
      }
    )
      .then(({ json }) => ({
        data: { ...params.data, id: json.id },
      }))
      .catch((err) => {
        const errorMsg = getErrorMessage(err);
        return Promise.reject(new HttpError(errorMsg, err.status, err.body));
      });
  },
  generateQr: (resource, params) => {
    if (resource !== "restaurants") {
      return;
    }
    return httpClient(`${REACT_APP_BASE_URL}/${resource}/${params.id}/qr`, {
      method: "POST",
    })
      .then(({ json }) => ({ data: json }))
      .catch((err) => {
        const errorMsg = getErrorMessage(err);
        return Promise.reject(new HttpError(errorMsg, err.status, err.body));
      });
  },
  generateMercadoPagoLink: (resource, params) => {
    if (resource !== "restaurants") {
      return;
    }
    return httpClient(
      `${REACT_APP_BASE_URL}/${resource}/${params.id}/link/generate`,
      {
        method: "POST",
      }
    )
      .then(({ json }) => ({ data: json }))
      .catch((err) => {
        const errorMsg = getErrorMessage(err);
        return Promise.reject(new HttpError(errorMsg, err.status, err.body));
      });
  },
  deliverPurchase: (resource, params) => {
    if (resource !== "purchases") {
      return;
    }
    return httpClient(
      `${REACT_APP_BASE_URL}/${resource}/${params.id}/deliver`,
      {
        method: "POST",
      }
    )
      .then(({ json }) => ({ data: json }))
      .catch((err) => {
        const errorMsg = getErrorMessage(err);
        return Promise.reject(new HttpError(errorMsg, err.status, err.body));
      });
  },
  refundPurchase: (resource, params) => {
    if (resource !== "purchases") {
      return;
    }
    return httpClient(`${REACT_APP_BASE_URL}/${resource}/${params.id}/refund`, {
      method: "POST",
      body: JSON.stringify({ paymentAmount: params.amount }),
    })
      .then(({ json }) => ({ data: json }))
      .catch((err) => {
        const errorMsg = getErrorMessage(err);
        return Promise.reject(new HttpError(errorMsg, err.status, err.body));
      });
  },
  partialRefundPurchase: (resource, params) => {
    if (resource !== "purchases") {
      return;
    }
    return httpClient(
      `${REACT_APP_BASE_URL}/${resource}/${params.id}/partial-refund`,
      {
        method: "POST",
        body: JSON.stringify({ products: params.products }),
      }
    )
      .then(({ json }) => ({ data: json }))
      .catch((err) => {
        const errorMsg = getErrorMessage(err);
        return Promise.reject(new HttpError(errorMsg, err.status, err.body));
      });
  },
  deliverReward: (resource, params) => {
    if (resource !== "redeemed-rewards") {
      return;
    }
    return httpClient(
      `${REACT_APP_BASE_URL}/${resource}/${params.id}/deliver`,
      {
        method: "POST",
        body: JSON.stringify({ deliveryCode: params.deliveryCode }),
      }
    )
      .then(({ json }) => ({ data: json }))
      .catch((err) => {
        const errorMsg = getErrorMessage(err);
        return Promise.reject(new HttpError(errorMsg, err.status, err.body));
      });
  },
  dismissReport: (resource, params) => {
    if (resource !== "reports") {
      return;
    }
    return httpClient(
      `${REACT_APP_BASE_URL}/${resource}/${params.id}/dismiss`,
      {
        method: "POST",
      }
    )
      .then(({ json }) => ({ data: json }))
      .catch((err) => {
        const errorMsg = getErrorMessage(err);
        return Promise.reject(new HttpError(errorMsg, err.status, err.body));
      });
  },
  banReport: (resource, params) => {
    if (resource !== "reviews") {
      return;
    }
    return httpClient(
      `${REACT_APP_BASE_URL}/${resource}/${params.reviewId}/ban`,
      {
        method: "POST",
        body: JSON.stringify({ report: params.reportId }),
      }
    )
      .then(({ json }) => ({ data: json }))
      .catch((err) => {
        const errorMsg = getErrorMessage(err);
        return Promise.reject(new HttpError(errorMsg, err.status, err.body));
      });
  },
  deactivateUser: (resource, params) => {
    if (resource !== "users") {
      return;
    }
    const body = params.note === undefined ? {} : { note: params.note };
    return httpClient(
      `${REACT_APP_BASE_URL}/${resource}/${params.id}/deactivate`,
      {
        method: "POST",
        body: JSON.stringify(body),
      }
    )
      .then(({ json }) => ({ data: json }))
      .catch((err) => {
        const errorMsg = getErrorMessage(err);
        return Promise.reject(new HttpError(errorMsg, err.status, err.body));
      });
  },
  confirmUser: (resource, params) => {
    if (resource !== "users") {
      return;
    }
    return httpClient(
      `${REACT_APP_BASE_URL}/${resource}/${params.id}/confirm-account`,
      {
        method: "POST",
      }
    );
  },
  getBillingDataFromCuit: async (resource, params) => {
    if (resource !== "billing") return;
    return httpClient(`${REACT_APP_BASE_URL}/${resource}/cuit/${params.cuit}`, {
      method: "GET",
    })
      .then(({ json }) => ({ data: json }))
      .catch((err) => {
        const errorMsg = getErrorMessage(err);
        return Promise.reject(new HttpError(errorMsg, err.status, err.body));
      });
  },

  deleteVideo: async (resource, videoId) => {
    if (resource !== "videos") return;
    return httpClient(`${REACT_APP_BASE_URL}/${resource}/${videoId}`, {
      method: "DELETE",
    })
      .then(({ json }) => ({ data: json }))
      .catch((err) => {
        return Promise.reject(new HttpError(err.body, err.status, err.body));
      });
  },
  deliverPurchases: (resource, params) => {
    if (resource !== "purchases") {
      return;
    }
    return httpClient(`${REACT_APP_BASE_URL}/${resource}/deliver`, {
      method: "POST",
      body: JSON.stringify({ purchases: params.purchaseIds }),
    })
      .then(({ json }) => ({ data: json }))
      .catch((err) => {
        const errorMsg = getErrorMessage(err);
        return Promise.reject(new HttpError(errorMsg, err.status, err.body));
      });
  },
  unlinkMercadoPago: (resource, params) => {
    if (resource !== "restaurants") {
      return;
    }
    return httpClient(
      `${REACT_APP_BASE_URL}/${resource}/${params.id}/mercadopago/unlink`,
      {
        method: "POST",
      }
    )
      .then(({ json }) => ({ data: json }))
      .catch((err) => {
        const errorMsg = getErrorMessage(err);
        return Promise.reject(new HttpError(errorMsg, err.status, err.body));
      });
  },
  createDraftVideo: (resource, params) => {
    if (resource !== "videos") {
      return;
    }
    return httpClient(`${REACT_APP_BASE_URL}/${resource}/draft`, {
      method: "POST",
      body: JSON.stringify({ name: params.name }),
    })
      .then(({ json }) => ({ data: json }))
      .catch((err) => {
        return Promise.reject(new HttpError(err.body, err.status, err.body));
      });
  },
  getVideoUploadLink: async (resource, params) => {
    if (resource !== "videos") return;

    return httpClient(
      `${REACT_APP_BASE_URL}/${resource}/${params.id}/upload-link?${stringify({
        type: params.type,
        contentType: params.contentType,
        fileName: params.fileName,
        ...(params.restaurants && { restaurants: params.restaurants }),
        ...(params.products && { products: params.products }),
      })}`,
      {
        method: "GET",
      }
    )
      .then(({ json }) => ({ data: json }))
      .catch((err) => {
        const errorMsg = getErrorMessage(err);
        return Promise.reject(new HttpError(errorMsg, err.status, err.body));
      });
  },
  uploadFileToS3: async (signedUrl, file, contentType) => {
    const headers = new Headers();
    headers.set("Content-Type", contentType);
    headers.set("Access-Control-Allow-Origin", "*");

    return fetchUtils
      .fetchJson(signedUrl, {
        method: "PUT",
        body: file.rawFile,
        headers,
      })
      .then(({ json }) => ({ data: json }))
      .catch((err) => {
        return Promise.reject(new HttpError(err.body, err.status, err.body));
      });
  },

  checkRegion: async ([lng, lat]) => {
    return httpClient(
      `${REACT_APP_BASE_URL}/regions/location?location=${JSON.stringify([
        lng,
        lat,
      ])}`,
      {
        method: "GET",
      }
    )
      .then(({ json }) => ({ data: json }))
      .catch((err) => {
        return Promise.reject(new HttpError(err.body, err.status, err.body));
      });
  },
  approveRestaurant: (resource, params) => {
    if (resource !== "restaurants") {
      return;
    }
    return httpClient(
      `${REACT_APP_BASE_URL}/${resource}/${params.id}/approve`,
      {
        method: "POST",
      }
    )
      .then(({ json }) => ({ data: json }))
      .catch((err) => {
        const errorMsg = getErrorMessage(err);
        return Promise.reject(new HttpError(errorMsg, err.status, err.body));
      });
  },

  updateStatus: (resource, params) => {
    if (resource !== "products" && resource !== "banners") {
      return;
    }
    const status = params.status;
    const isValidStatus = Object.values(ENTITY_AVAILABILITY_STATUS).includes(
      status
    );
    if (!isValidStatus) {
      return;
    }
    return httpClient(`${REACT_APP_BASE_URL}/${resource}/status`, {
      method: "PUT",
      body: JSON.stringify({
        ...(resource === "products" ? { products: params.productIds } : {}),
        ...(resource === "banners" ? { banners: params.bannerIds } : {}),
        status,
      }),
    })
      .then(({ json }) => ({ data: json }))
      .catch((err) => {
        const errorMsg = getErrorMessage(err);
        return Promise.reject(new HttpError(errorMsg, err.status, err.body));
      });
  },

  deactivateRestaurantProducts: (resource, params) => {
    if (resource !== "restaurants") {
      return;
    }
    return httpClient(
      `${REACT_APP_BASE_URL}/${resource}/${params.id}/products/deactivate`,
      {
        method: "PUT",
      }
    )
      .then(({ json }) => ({ data: json }))
      .catch((err) => {
        const errorMsg = getErrorMessage(err);
        return Promise.reject(new HttpError(errorMsg, err.status, err.body));
      });
  },
};

export default dataProvider;
